import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { paths } from "utils/constants";
import { apiDeleteModel } from "api/apiAiModels";
import { useNavigate } from "react-router-dom";
import CardPlaceholder from "components/UI/GenericCard/CardPlaceholder";
import { Alert, App, Button, Switch } from "antd";
import { ExclamationCircleFilled, SwapOutlined } from "@ant-design/icons";
import { toFixed } from "utils/helpers";
import HoverableTooltip from "components/UI/Tooltip/HoverableTooltip";

const ModelCreationStatus = (props) => {
  const {
    requestStatus,
    setRequestStatus,
    isPredicting,
    predictAllModelHistory,
    modelData,
    setModelData,
  } = props;

  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const [deleteModelPending, setDeleteModelPending] = useState(false);
  const [previousModelData, setPreviousModelData] = useState(null);
  const [showComparison, setShowComparison] = useState(false);
  const [currentModelData, setCurrentModelData] = useState(null);

  useEffect(() => {
    if (modelData && JSON.stringify(modelData) !== JSON.stringify(currentModelData)) {
      if (currentModelData) {
        // Store the current model as previous before updating
        setPreviousModelData(currentModelData);
        setShowComparison(true);
      }
      setCurrentModelData(modelData);
    }
  }, [modelData, currentModelData]);

  const getMetricChange = (currentVal, previousVal) => {
    if (previousVal === undefined || currentVal === undefined) return null;
    const current = parseFloat(currentVal);
    const previous = parseFloat(previousVal);
    if (isNaN(current) || isNaN(previous)) return null;
    const change = current - previous;
    return {
      value: change,
      percentage: ((change / previous) * 100),
      isImprovement: change > 0
    };
  };

  const renderMetricWithComparison = (label, currentValue, previousValue, tooltip, isRatio = false) => {
    const change = getMetricChange(currentValue, previousValue);
    const showChange = showComparison && change !== null && previousValue !== undefined;
    // ratios (lift, AUC) are shown as plain numbers, the rest as percentages
    const format = (value) =>
      value == null ? "-" : isRatio ? toFixed(value, 2) : `${toFixed(value * 100)}%`;

    return (
      <div className="bg-gray-50 p-3 rounded-lg">
        <div className="flex items-center gap-1 mb-1">
          <p className="text-sm text-gray-600">{label}</p>
          <HoverableTooltip text={tooltip} />
        </div>
        <div className="flex items-center gap-2">
          <p className={`text-lg font-semibold ${showChange ? (change.isImprovement ? 'text-green-700' : 'text-red-700') : ''}`}>
            {format(currentValue)}
          </p>
          {showChange && (
            <div className={`text-sm px-2 py-1 rounded ${change.isImprovement ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {change.isImprovement ? '+' : ''}{format(change.value)}
              <SwapOutlined className="ml-1" />
            </div>
          )}
        </div>
        {showChange && (
          <p className="text-xs text-gray-500 mt-1">
            Previous: {format(previousValue)}
          </p>
        )}
      </div>
    );
  };

  const onUseCreatedModel = () => {
    navigate(paths.ai);
  };

  const deleteModelById = async () => {
    try {
      setDeleteModelPending(true);
      await apiDeleteModel(requestStatus);
      setRequestStatus("");
      setModelData(null);
      message.success("Model deleted successfully");
    } catch (err) {
      message.error("Error deleting model");
    }
    setDeleteModelPending(false);
  };

  const showDeleteConfirm = () => {
    modal.confirm({
      title: "Are you sure you want to delete this model?",
      icon: <ExclamationCircleFilled />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        deleteModelById();
      },
      onCancel() {},
    });
  };

  const returnStatus = () => {
    console.log(requestStatus);
    if (!requestStatus) return null;
    else if (requestStatus === "error") {
      // model creation failed
      return (
        <Alert
          type="error"
          message="Failed to create model!"
          showIcon
          description="Try selecting different dates."
        />
      );
    } else if (isPredicting) {
      // model is predicting
      return (
        <Alert
          type="info"
          message="Model is predicting..."
          description="Wait a few moments until the model finishes predicting."
          showIcon
          closable
        />
      );
    } else if (
      requestStatus !== "" &&
      requestStatus !== "error" &&
      requestStatus !== "success"
    ) {
      // model created successfully need to change content
      if (modelData.results.eval_only) {
        return (
          <Alert
            type="success"
            message="Model created successfully!"
            description={
              <div>
                <p>This model is in evaluation mode.</p>
                <p>You can view the results below.</p>
              </div>
            }
            showIcon
          />
        );
      }
      return (
        <Alert
          type="success"
          message="Model created successfully!"
          description={
            <div>
              <p>Click the button below to save the predictions.</p>
              <p>You can also delete the model if you want.</p>
            </div>
          }
          action={
            <div className="flex flex-col gap-4">
              <Button type="dashed" onClick={predictAllModelHistory}>
                Save predictions
              </Button>
              <Button
                danger
                ghost
                onClick={() => showDeleteConfirm()}
                loading={deleteModelPending}
              >
                Delete model
              </Button>
            </div>
          }
          // showIcon
          closable
        />
      );
    } else if (requestStatus === "success") {
      // model finished predicting
      return (
        <Alert
          type="success"
          message="Model created successfully!"
          description="You can view you new model in AI Backtests page."
          showIcon
          action={
            <Button size="small" type="text" onClick={onUseCreatedModel}>
              Try it now!
            </Button>
          }
          closable
        />
      );
    }
  };

  return (
    <CardPlaceholder>
      {returnStatus()}
      {modelData && (
        <div className="pt-4 space-y-6 flex justify-between pb-4">
          {/* Evaluation Metrics */}
          {!modelData.results.evaluation_metrics ? (
            <div>
              <h3 className="text-lg font-semibold">No test period</h3>
              <p className="text-sm text-gray-600">
                No data after the training range ({modelData.results.test_start_date} to{" "}
                {modelData.results.test_end_date}). Pick an earlier end date to get test stats.
              </p>
            </div>
          ) : (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                Evaluation Metrics on Test Data
              </h3>
              {previousModelData && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Compare with previous:</span>
                  <Switch
                    checked={showComparison}
                    onChange={setShowComparison}
                    size="small"
                  />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {renderMetricWithComparison(
                "Accuracy",
                modelData.results.evaluation_metrics.accuracy,
                previousModelData?.results?.evaluation_metrics?.accuracy,
                "The percentage of correct predictions out of all predictions made by the model."
              )}
              {renderMetricWithComparison(
                "Precision",
                modelData.results.evaluation_metrics.precision,
                previousModelData?.results?.evaluation_metrics?.precision,
                "The percentage of positive predictions that were actually correct. High precision means fewer false positives."
              )}
              {renderMetricWithComparison(
                "Recall",
                modelData.results.evaluation_metrics.recall,
                previousModelData?.results?.evaluation_metrics?.recall,
                "The percentage of actual positive cases that were correctly identified. High recall means fewer false negatives."
              )}
              {renderMetricWithComparison(
                "F1 Score",
                modelData.results.evaluation_metrics.f1_score,
                previousModelData?.results?.evaluation_metrics?.f1_score,
                "The harmonic mean of precision and recall. A balanced measure that considers both false positives and false negatives."
              )}
              {renderMetricWithComparison(
                "Specificity",
                modelData.results.evaluation_metrics.specificity,
                previousModelData?.results?.evaluation_metrics?.specificity,
                "The percentage of actual negative cases that were correctly identified. High specificity means fewer false positives."
              )}
              {renderMetricWithComparison(
                "Balanced Accuracy",
                modelData.results.evaluation_metrics.balanced_accuracy,
                previousModelData?.results?.evaluation_metrics?.balanced_accuracy,
                "The average of recall and specificity. Useful for imbalanced datasets as it accounts for both positive and negative class performance."
              )}
              {renderMetricWithComparison(
                "Test Buy Rate",
                modelData.results.evaluation_metrics.test_base_rate,
                previousModelData?.results?.evaluation_metrics?.test_base_rate,
                "Share of days in the test range that actually were buys. Precision only means something compared to this."
              )}
              {renderMetricWithComparison(
                "Lift",
                modelData.results.evaluation_metrics.lift,
                previousModelData?.results?.evaluation_metrics?.lift,
                "Precision divided by the test buy rate. 1.00 = no better than buying every day; higher is better.",
                true
              )}
              {renderMetricWithComparison(
                "ROC AUC",
                modelData.results.evaluation_metrics.roc_auc,
                previousModelData?.results?.evaluation_metrics?.roc_auc,
                "How well the buy probability ranks real buy days above no-buy days, at any threshold. 0.50 = random.",
                true
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Test range {modelData.results.test_start_date} to {modelData.results.test_end_date}:{" "}
              {modelData.results.evaluation_metrics.test_rows} rows, about{" "}
              {modelData.results.evaluation_metrics.effective_samples} independent samples
            </p>
          </div>
          )}

          {/* Feature Importance */}
          {modelData.results.feature_importance && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <h4 className="text-md font-semibold">Feature Importance</h4>
                <HoverableTooltip text="Shows how much each technical indicator contributes to the model's predictions. Higher values indicate more important features for making trading decisions." />
              </div>
              <div className="space-y-2">
                {modelData.results.feature_importance.map(
                  ([feature, importance]) => (
                    <div key={feature} className="flex items-center space-x-3">
                      <div className="w-32 text-sm font-medium">{feature}</div>
                      <div className="flex-1 bg-gray-200 rounded-full h-4 relative">
                        <div
                          className="bg-blue-500 h-4 rounded-full transition-all duration-300"
                          style={{ width: `${importance * 100}%` }}
                        ></div>
                      </div>
                      <div className="w-16 text-sm text-gray-600 text-right">
                        {toFixed(importance * 100)}%
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {/* Confusion Matrix */}
          {modelData.results.evaluation_metrics && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <h4 className="text-md font-semibold">Confusion Matrix</h4>
              <HoverableTooltip text="A table showing the breakdown of correct and incorrect predictions. True Positive (TP): Correctly predicted positive cases. True Negative (TN): Correctly predicted negative cases. False Positive (FP): Incorrectly predicted as positive. False Negative (FN): Incorrectly predicted as negative." />
            </div>
            <div className="grid grid-cols-2 gap-2 max-w-xs">
              <div className="bg-green-100 p-3 rounded-lg text-center">
                <p className="text-sm text-green-700">True Positive</p>
                <p className="font-semibold">
                  {
                    modelData.results.evaluation_metrics.confusion_matrix
                      .tp_count
                  }
                </p>
                <p className="text-xs text-gray-600">
                  (
                  {toFixed(
                    modelData.results.evaluation_metrics.confusion_matrix.tp *
                      100
                  )}
                  %)
                </p>
              </div>
              <div className="bg-red-100 p-3 rounded-lg text-center">
                <p className="text-sm text-red-700">False Positive</p>
                <p className="font-semibold">
                  {
                    modelData.results.evaluation_metrics.confusion_matrix
                      .fp_count
                  }
                </p>
                <p className="text-xs text-gray-600">
                  (
                  {toFixed(
                    modelData.results.evaluation_metrics.confusion_matrix.fp *
                      100
                  )}
                  %)
                </p>
              </div>
              <div className="bg-red-100 p-3 rounded-lg text-center">
                <p className="text-sm text-red-700">False Negative</p>
                <p className="font-semibold">
                  {
                    modelData.results.evaluation_metrics.confusion_matrix
                      .fn_count
                  }
                </p>
                <p className="text-xs text-gray-600">
                  (
                  {toFixed(
                    modelData.results.evaluation_metrics.confusion_matrix.fn *
                      100
                  )}
                  %)
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg text-center">
                <p className="text-sm text-green-700">True Negative</p>
                <p className="font-semibold">
                  {
                    modelData.results.evaluation_metrics.confusion_matrix
                      .tn_count
                  }
                </p>
                <p className="text-xs text-gray-600">
                  (
                  {toFixed(
                    modelData.results.evaluation_metrics.confusion_matrix.tn *
                      100
                  )}
                  %)
                </p>
              </div>
            </div>
          </div>
          )}
        </div>
      )}
    </CardPlaceholder>
  );
};

ModelCreationStatus.propTypes = {
  requestStatus: PropTypes.string.isRequired,
  setRequestStatus: PropTypes.func.isRequired,
  isPredicting: PropTypes.bool.isRequired,
  predictAllModelHistory: PropTypes.func.isRequired,
  modelData: PropTypes.object,
  setModelData: PropTypes.func.isRequired,
};

export default ModelCreationStatus;
